import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  ArrowPathIcon,
  DocumentArrowUpIcon,
  ExclamationTriangleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Spinner from '@/components/ui/Spinner';
import EmptyState from '@/components/ui/EmptyState';
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeaderCell,
  TableCell,
} from '@/components/ui/Table';
import { useApi } from '@/hooks/useApi';
import { COVERS_BASE_URL, apiClient } from '@/lib';
import type { Book, BooksListResponse, BookStatus } from '@/types/api';

const statusOptions = [
  { value: '', label: 'All Status' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'PROCESSING', label: 'Processing' },
  { value: 'INGESTED', label: 'Ingested' },
  { value: 'TRANSFORMED', label: 'Transformed' },
  { value: 'VALIDATED', label: 'Validated' },
  { value: 'PUBLISHED', label: 'Published' },
  { value: 'FEED_UPDATED', label: 'Feed Updated' },
  { value: 'PUSHED', label: 'Pushed' },
  { value: 'PULLED_CONFIRMED', label: 'Pull Confirmed' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'ERROR', label: 'Error' },
];

function getStatusBadge(status: BookStatus | null) {
  if (!status) return <Badge variant="slate">Unknown</Badge>;

  switch (status) {
    case 'PUBLISHED':
    case 'PUSHED':
    case 'PULLED_CONFIRMED':
      return <Badge variant="success" dot>{status}</Badge>;
    case 'PENDING':
    case 'PROCESSING':
    case 'INGESTED':
    case 'TRANSFORMED':
      return <Badge variant="warning" dot>{status}</Badge>;
    case 'VALIDATED':
    case 'FEED_UPDATED':
      return <Badge variant="primary" dot>{status}</Badge>;
    case 'FAILED':
    case 'CANCELLED':
    case 'ERROR':
      return <Badge variant="danger" dot>{status}</Badge>;
    default:
      return <Badge variant="slate">{status}</Badge>;
  }
}

function getBookCoverUrl(book: Book): string | null {
  const base = COVERS_BASE_URL.replace(/\/$/, '');
  const key = book.cover_key;
  if (key && key !== 'placeholders/default.jpg') {
    return `${base}/${key}`;
  }
  // Fallback: cover from kampus-incoming/staging/{publisher}/{isbn}/
  if (book.publisher_name && book.isbn) {
    return `${base}/staging/${encodeURIComponent(book.publisher_name)}/${book.isbn}`;
  }
  return null;
}

const PAGE_SIZE = 15;

export default function DistBooksPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
  const [debouncedSearch, setDebouncedSearch] = useState(search);

  // Global status totals from backend (all books, not just current page)
  const [statusTotals, setStatusTotals] = useState({
    all: 0,
    pending: 0,
    published: 0,
    errors: 0,
  });

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      // Reset to page 1 when search changes
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset to page 1 when status filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter]);

  // Build API endpoint with query params
  const apiEndpoint = useMemo(() => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('q', debouncedSearch);
    if (statusFilter) params.set('status', statusFilter);
    params.set('page', String(currentPage));
    params.set('pageSize', String(PAGE_SIZE));
    const queryString = params.toString();
    return `distribution/books${queryString ? `?${queryString}` : ''}`;
  }, [debouncedSearch, statusFilter, currentPage]);

  // Fetch books from API
  const { data, isLoading, error, refetch } = useApi<BooksListResponse>(apiEndpoint);

  // Update URL search params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('q', debouncedSearch);
    if (statusFilter) params.set('status', statusFilter);
    if (currentPage > 1) params.set('page', String(currentPage));
    setSearchParams(params, { replace: true });
  }, [debouncedSearch, statusFilter, currentPage, setSearchParams]);

  const books = data?.items || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  // Fetch backend-wide totals for status tabs
  useEffect(() => {
    let isCancelled = false;

    async function fetchStatusTotals() {
      try {
        const [allRes, pendingRes, publishedRes, failedRes] = await Promise.all([
          apiClient.get<BooksListResponse>('distribution/books?page=1&pageSize=1'),
          apiClient.get<BooksListResponse>('distribution/books?status=PENDING&page=1&pageSize=1'),
          apiClient.get<BooksListResponse>('distribution/books?status=PUBLISHED&page=1&pageSize=1'),
          apiClient.get<BooksListResponse>('distribution/books?status=FAILED&page=1&pageSize=1'),
        ]);

        if (isCancelled) return;

        setStatusTotals({
          all: allRes.total ?? 0,
          pending: pendingRes.total ?? 0,
          published: publishedRes.total ?? 0,
          errors: failedRes.total ?? 0,
        });
      } catch (err) {
        if (isCancelled) return;
        console.error('Failed to load global book status totals', err);
        // Fallback: keep existing totals; "All" will still use current page total below
      }
    }

    fetchStatusTotals();

    return () => {
      isCancelled = true;
    };
  }, []);

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | 'ellipsis')[] = [];
    const maxVisiblePages = 7;
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      if (currentPage > 3) {
        pages.push('ellipsis');
      }
      
      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (currentPage < totalPages - 2) {
        pages.push('ellipsis');
      }
      
      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  // Status counts for tabs - prefer backend totals, fall back to current page total for "All"
  const statusCounts = useMemo(() => {
    return {
      all: statusTotals.all || total,
      pending: statusTotals.pending,
      published: statusTotals.published,
      errors: statusTotals.errors,
    };
  }, [statusTotals, total]);

  const handleStatusTabClick = (status: string) => {
    setStatusFilter(status);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Book Catalog</h1>
          <p className="text-slate-600 dark:text-slate-300 mt-1">
            Manage and publish your book catalog
            {total > 0 && <span className="ml-2 text-slate-500 dark:text-slate-400">({total} books)</span>}
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            leftIcon={<ArrowPathIcon className="w-4 h-4" />}
            onClick={() => refetch()}
            disabled={isLoading}
          >
            Refresh
          </Button>
          <Button
            variant="outline"
            leftIcon={<DocumentArrowUpIcon className="w-4 h-4" />}
            disabled
          >
            Build Feed
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card padding="md">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search by title or ISBN..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftIcon={<MagnifyingGlassIcon className="w-5 h-5" />}
            />
          </div>
          <div className="flex flex-wrap gap-3">
            <Select
              options={statusOptions}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-40"
            />
          </div>
        </div>
      </Card>

      {/* Status Tabs */}
      <div className="flex gap-2 border-b border-slate-200 dark:border-slate-800 pb-2">
        <button
          onClick={() => handleStatusTabClick('')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            statusFilter === ''
              ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-300'
              : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
          }`}
        >
          All ({statusCounts.all})
        </button>
        <button
          onClick={() => handleStatusTabClick('PENDING')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            statusFilter === 'PENDING'
              ? 'bg-warning-100 dark:bg-warning-900/30 text-warning-600 dark:text-warning-300'
              : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
          }`}
        >
          Pending ({statusCounts.pending})
        </button>
        <button
          onClick={() => handleStatusTabClick('PUBLISHED')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            statusFilter === 'PUBLISHED'
              ? 'bg-success-100 dark:bg-success-900/30 text-success-600 dark:text-success-300'
              : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
          }`}
        >
          Published ({statusCounts.published})
        </button>
        <button
          onClick={() => handleStatusTabClick('FAILED')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            statusFilter === 'FAILED'
              ? 'bg-danger-100 dark:bg-danger-900/30 text-danger-600 dark:text-danger-300'
              : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
          }`}
        >
          Errors ({statusCounts.errors})
        </button>
      </div>

      {/* Error State */}
      {error && (
        <Card padding="lg">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <ExclamationTriangleIcon className="w-12 h-12 text-danger-500 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">Failed to load books</h3>
            <p className="text-slate-600 dark:text-slate-300 mb-4">{error}</p>
            <Button onClick={() => refetch()} leftIcon={<ArrowPathIcon className="w-4 h-4" />}>
              Try Again
            </Button>
          </div>
        </Card>
      )}

      {/* Loading State */}
      {isLoading && !data && (
        <Card padding="lg">
          <div className="flex flex-col items-center justify-center py-12">
            <Spinner size="lg" />
            <p className="text-slate-600 dark:text-slate-300 mt-4">Loading books...</p>
          </div>
        </Card>
      )}

      {/* Empty State */}
      {!isLoading && !error && books.length === 0 && (
        <EmptyState
          icon={<DocumentArrowUpIcon className="w-12 h-12" />}
          title="No books found"
          description={
            debouncedSearch || statusFilter
              ? "Try adjusting your search or filters"
              : "Get started by adding your first book to the catalog"
          }
          action={
            debouncedSearch || statusFilter ? (
              <Button
                variant="outline"
                onClick={() => {
                  setSearch('');
                  setStatusFilter('');
                }}
              >
                Clear Filters
              </Button>
            ) : undefined
          }
        />
      )}

      {/* Books Table */}
      {!error && books.length > 0 && (
        <>
          <Card padding="none">
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow isHoverable={false}>
                    <TableHeaderCell>Cover</TableHeaderCell>
                    <TableHeaderCell>Book</TableHeaderCell>
                    <TableHeaderCell>ISBN</TableHeaderCell>
                    <TableHeaderCell>Publisher</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                    <TableHeaderCell>Version</TableHeaderCell>
                    <TableHeaderCell>Actions</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {books.map((book) => (
                    <TableRow key={book.id}>
                      <TableCell>
                        {(() => {
                          const url = getBookCoverUrl(book);
                          return url ? (
                            <img src={url} alt={book.title} className="w-12 h-16 rounded-lg object-cover shadow-sm" />
                          ) : (
                            <div className="w-12 h-16 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 text-xs">—</div>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <Link
                          to={`/distribution/books/${book.id}`}
                          className="hover:text-primary-500 transition-colors"
                        >
                          <p className="font-medium text-slate-900 dark:text-slate-100">{book.title}</p>
                          {book.subtitle && (
                            <p className="text-xs text-slate-600 dark:text-slate-300">{book.subtitle}</p>
                          )}
                          {book.language && (
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{book.language.toUpperCase()}</p>
                          )}
                        </Link>
                      </TableCell>
                      <TableCell className="font-mono text-sm text-slate-700 dark:text-slate-200">{book.isbn}</TableCell>
                      <TableCell className="text-slate-700 dark:text-slate-200">
                        {book.publisher_name || <span className="text-slate-500 dark:text-slate-400">—</span>}
                      </TableCell>
                      <TableCell>{getStatusBadge(book.status)}</TableCell>
                      <TableCell className="text-slate-700 dark:text-slate-200">v{book.version}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Link to={`/distribution/books/${book.id}`}>
                            <Button variant="ghost" size="sm">View</Button>
                          </Link>
                          {(book.status === 'FAILED' || book.status === 'ERROR') && (
                            <Button variant="outline" size="sm" leftIcon={<ArrowPathIcon className="w-3 h-3" />}>
                              Retry
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          {/* Pagination */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Showing {((currentPage - 1) * PAGE_SIZE) + 1} - {Math.min(currentPage * PAGE_SIZE, total)} of {total} books
              {isLoading && <span className="ml-2">(refreshing...)</span>}
            </p>
            
            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                {/* Previous Button */}
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className={`p-2 rounded-lg transition-colors ${
                    currentPage === 1 
                      ? 'text-slate-300 dark:text-slate-600 cursor-not-allowed' 
                      : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                  }`}
                  aria-label="Previous page"
                >
                  <ChevronLeftIcon className="w-5 h-5" />
                </button>

                {/* Page Numbers */}
                <div className="flex items-center gap-1">
                  {getPageNumbers().map((page, index) => (
                    page === 'ellipsis' ? (
                      <span key={`ellipsis-${index}`} className="px-2 text-slate-500 dark:text-slate-500">...</span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`min-w-[36px] h-9 px-3 rounded-lg text-sm font-medium transition-colors ${
                          currentPage === page
                            ? 'bg-primary-500 text-white'
                            : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                        }`}
                      >
                        {page}
                      </button>
                    )
                  ))}
                </div>

                {/* Next Button */}
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className={`p-2 rounded-lg transition-colors ${
                    currentPage === totalPages 
                      ? 'text-slate-300 dark:text-slate-600 cursor-not-allowed' 
                      : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                  }`}
                  aria-label="Next page"
                >
                  <ChevronRightIcon className="w-5 h-5" />
                </button>
              </div>
            )}

            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled>
                Approve Selected
              </Button>
              <Button variant="outline" size="sm" disabled>
                Export
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
